import type { ResultOf } from '@graphql-typed-document-node/core';
import { getFragmentData, gql, type FragmentType } from '@plato/app-core/gql';
import type {
    CustomerListTable_CustomerConnectionFragment,
    CustomerListTableQuery,
} from '@plato/app-core/gql/graphql';
import type { CustomerSortBy } from '@plato/app-core/gql/schema-types';
import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useInfiniteQuery, type NodeTypeOf } from '@plato/app-core/hooks/use-infinite-query';
import { useIsManagerView, useViewMode } from '@plato/app-core/hooks/use-view-mode';
import { CurrencyOrEmpty } from '@plato/app-shared/components/currency-or-empty';
import { CustomerStatusBadge } from '@plato/app-shared/components/customer-status-badge';
import { FromNow } from '@plato/app-shared/components/from-now';
import { MaskedContent } from '@plato/app-shared/components/masked-content';
import {
    MetricHintExplanation,
    useMetricHintOpenedTracking,
} from '@plato/app-shared/components/metric-explainer';
import { useDefaultMetric } from '@plato/app-shared/hooks/use-default-metric';
import {
    opportunityInsightsFilter,
    opportunityInsightsSortBy,
    ordersFilter,
    riskInsightsFilter,
    riskInsightsSortBy,
    ttmRevenueFilter,
    visitsFilter,
} from '@plato/app-shared/utils/customer-list';
import { omitEmpty } from '@plato/shared-util/object';
import { DateText } from '@plato/ui-legacy/components/common/date-time-text';
import { DoubleDeckerCell } from '@plato/ui-legacy/components/list-views/double-decker-cell';
import { IdAndNameCell } from '@plato/ui-legacy/components/list-views/id-and-name-cell';
import { MarginPercentage } from '@plato/ui-legacy/components/list-views/margin-percentage';
import { DataText } from '@plato/ui-legacy/components/typography/data-text';
import { DeltaText } from '@plato/ui-legacy/components/typography/delta-text';
import { DeltaTriangle } from '@plato/ui-legacy/components/typography/delta-triangle';
import { NumberOrEmpty } from '@plato/ui-legacy/components/typography/number-or-empty';
import { PercentageOrEmpty } from '@plato/ui-legacy/components/typography/percentage-or-empty';
import { createColumnHelper, DataTable, type ColumnDef } from '@plato/ui/components/data-table';
import { isEqual } from 'lodash-es';
import { useCallback, useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '../hooks/use-analytics.ts';
import { useCustomerListParams } from '../hooks/use-customer-list-params.ts';

const PAGE_QUERY = gql(/* GraphQL */ `
    query CustomerListTable(
        $filter: CustomerFilter
        $sortBy: CustomerSort!
        $first: Int
        $after: String
        $ttmRevenueFilter: SalesMetricFilter!
        $defaultMetricFilter: SalesMetricFilter!
        $visitsFilter: NoteFilter!
        $ordersFilter: SalesDocumentsFilter!
        $opportunityInsightsFilter: InsightFilter!
        $riskInsightsFilter: InsightFilter!
        $includeVisits: Boolean!
        $includePlatoStatus: Boolean!
        $includeGrossMargin: Boolean!
        $includeSalesReps: Boolean!
    ) {
        customers(filter: $filter, sortBy: $sortBy, first: $first, after: $after) {
            nodes {
                id
                name
                industrySector
                status @include(if: $includePlatoStatus)
                primaryAddress {
                    city
                }
                salesMetric(filter: $defaultMetricFilter) {
                    totalAmount
                    deltaTotalAmount
                    deltaPercentTotalAmount
                    grossMargin @include(if: $includeGrossMargin)
                    deltaGrossMargin @include(if: $includeGrossMargin)
                    deltaPercentGrossMargin @include(if: $includeGrossMargin)
                }
                visits: notes(filter: $visitsFilter) @include(if: $includeVisits) {
                    totalCount
                    lastOccurredAt
                }
                visitsPerRevenue: notesPerSalesMetric(notes: $visitsFilter, salesMetric: $ttmRevenueFilter)
                    @include(if: $includeVisits)
                opportunityInsights: insights(filter: $opportunityInsightsFilter) {
                    totalAmount
                }
                riskInsights: insights(filter: $riskInsightsFilter) {
                    totalAmount
                }
                salesReps @include(if: $includeSalesReps) {
                    id
                    name
                }
                lastOrder: salesDocuments(filter: $ordersFilter) {
                    lastDate
                }
            }
            pageInfo {
                hasNextPage
                endCursor
            }
        }
    }
`);

const AGGREGATES_FRAGMENT = gql(/* GraphQL */ `
    fragment CustomerListTable_CustomerConnection on CustomerConnection {
        ... on CustomerConnection @include(if: $includeSummary) {
            salesMetric(filter: $defaultMetricFilter) {
                totalAmount
                deltaTotalAmount
                deltaPercentTotalAmount
                grossMargin @include(if: $includeGrossMargin)
                deltaGrossMargin @include(if: $includeGrossMargin)
                deltaPercentGrossMargin @include(if: $includeGrossMargin)
            }
        }
    }
`);

type CustomerSummaryData = ResultOf<typeof AGGREGATES_FRAGMENT>;

type CustomerListColumnMeta = {
    aiChat?: {
        description?: string;
        requires?: readonly (keyof ReturnType<typeof useAppConfig>)[];
        managerOnly?: boolean;
    };
};
type CustomerRowData = ResultOf<typeof PAGE_QUERY>['customers']['nodes'][number];

export type CustomerColumnDef = ColumnDef<
    CustomerRowData,
    CustomerSummaryData,
    CustomerSortBy,
    CustomerListColumnMeta
>;

const columnHelper = createColumnHelper<
    CustomerRowData,
    CustomerSummaryData,
    CustomerSortBy,
    CustomerListColumnMeta
>();

type Props = {
    summary: FragmentType<typeof AGGREGATES_FRAGMENT> | undefined;
};

export function CustomerListTable({ summary: summaryData }: Props): ReactElement {
    const { t } = useTranslation();
    const { params, setParams } = useCustomerListParams();
    const { sorting, ...filter } = params;
    const summary = getFragmentData(AGGREGATES_FRAGMENT, summaryData);
    const { canReadSalesRepAssignment, enablePlatoStatus, canReadGrossMargin } = useAppConfig();
    const isManagerView = useIsManagerView();
    const sendEvent = useAnalytics();
    const defaultMetricFilter = useDefaultMetric();
    const columns = useCustomerListColumns();

    const { nodes, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteQuery({
        query: PAGE_QUERY,
        getConnection: (data) => data.customers,
        variables: {
            filter: omitEmpty(filter),
            sortBy: sorting,
            ttmRevenueFilter,
            defaultMetricFilter,
            visitsFilter,
            ordersFilter,
            opportunityInsightsFilter,
            riskInsightsFilter,
            includeVisits: isManagerView,
            includePlatoStatus: enablePlatoStatus,
            includeGrossMargin: canReadGrossMargin,
            includeSalesReps: canReadSalesRepAssignment,
        },
        pageSize: 100,
    });

    const hasTotals = Boolean(summary?.salesMetric);

    return (
        <DataTable
            data={nodes}
            columns={columns}
            isLoading={isLoading}
            emptyLabel={t('empty_table__no_results')}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onFetchNextPage={fetchNextPage}
            sorting={sorting}
            onSortingChange={(nextSorting, columnId) => {
                setParams((current) => ({ ...current, sorting: nextSorting ?? current.sorting }));

                if (nextSorting && columnId) {
                    sendEvent('customer_list_sort_used', {
                        column: columnId,
                        direction: nextSorting.direction === 'desc' ? 'DESC' : 'ASC',
                    });
                }
            }}
            summary={hasTotals ? summary : undefined}
        />
    );
}

type CustomerListColumn = ColumnDef<
    NodeTypeOf<CustomerListTableQuery['customers']>,
    CustomerListTable_CustomerConnectionFragment,
    CustomerSortBy,
    CustomerListColumnMeta
>;

function useCustomerListColumns(): readonly CustomerListColumn[] {
    const { params } = useCustomerListParams();
    const { sorting } = params;
    const { t } = useTranslation('customer-list');
    const trackMetricHintOpened = useMetricHintOpenedTracking();
    const {
        canReadSalesRepAssignment,
        enablePlatoStatus,
        canReadGrossMargin,
        defaultTimeframe,
        defaultMetric,
    } = useAppConfig();
    const isManagerView = useIsManagerView();
    const [viewMode] = useViewMode();
    const sendEvent = useAnalytics();
    const isOrderSumMetric = defaultMetric === 'Order_Sum';
    const isDefaultTimeframeTtm = defaultTimeframe === 'TTM';
    const defaultMetricFilter = useDefaultMetric();
    const metricBase = defaultMetricFilter.basis;

    const defaultMetricTitle = isOrderSumMetric
        ? isDefaultTimeframeTtm
            ? t('table_header__order_sum_ttm_current')
            : t('table_header__order_sum_ytd')
        : isDefaultTimeframeTtm
          ? t('table_header__ttm_current')
          : t('table_header__revenue_ytd');

    const trackVisitTooltipOpened = useCallback(
        (column: 'visits_per_100k' | 'last_visit') => {
            sendEvent('customer_list_visit_column_tooltip_opened', {
                column,
                view_mode: viewMode,
            });
        },
        [sendEvent, viewMode],
    );

    return useMemo(
        () =>
            columnHelper.columns([
                columnHelper.accessor((row) => row.name, {
                    id: 'customerName',
                    header: t('table_header__name'),
                    cell: ({ row, getValue }) => {
                        const { id } = row.original;
                        return (
                            <IdAndNameCell
                                to={`/customers/${encodeURIComponent(id)}`}
                                id={id}
                                title={getValue()}
                            />
                        );
                    },
                    size: 250,
                    meta: {
                        pinned: 'left',
                        sortBy: { field: 'customer_name' },
                        summary: () => t('table_footer__sum'),
                    },
                }),
                columnHelper.accessor((row) => row.status, {
                    id: 'status',
                    meta: {
                        enabled: enablePlatoStatus,
                        sortBy: { field: 'status' },
                        aiChat: {
                            description:
                                'Plato status from its order-activity model: new, active, at risk or dormant, or inactive when marked so by hand. Not an ERP status',
                            requires: ['enablePlatoStatus'],
                        },
                        hint: (
                            <div className="flex flex-col gap-tight">
                                <p>{t('status_tooltip')}</p>
                                <ul className="list-inside list-disc space-y-tight">
                                    <li>
                                        <strong>{t('status_new')}</strong> - {t('status_tooltip_new')}
                                    </li>
                                    <li>
                                        <strong>{t('status_active')}</strong> - {t('status_tooltip_active')}
                                    </li>
                                    <li>
                                        <strong>{t('status_at_risk')}</strong> - {t('status_tooltip_at_risk')}
                                    </li>
                                    <li>
                                        <strong>{t('status_dormant')}</strong> - {t('status_tooltip_dormant')}
                                    </li>
                                    <li>
                                        <strong>{t('status_inactive')}</strong> -{' '}
                                        {t('status_tooltip_inactive')}
                                    </li>
                                </ul>
                            </div>
                        ),
                    },
                    header: t('table_header__status'),
                    cell: ({ getValue }) => {
                        const status = getValue();
                        return status ? <CustomerStatusBadge status={status} /> : null;
                    },
                    size: 120,
                }),
                columnHelper.accessor((row) => row.visits?.totalCount, {
                    id: 'documentedVisits',
                    header: t('table_header__visit_count'),
                    cell: ({ getValue }) => (
                        <DataText>
                            <NumberOrEmpty decimals={0}>{getValue()}</NumberOrEmpty>
                        </DataText>
                    ),
                    meta: {
                        enabled: isManagerView,
                        sortBy: {
                            notes: { field: 'total_count', dimension: visitsFilter },
                        },
                        aiChat: {
                            description:
                                'Visits logged in Plato in the last 12 months, including visits where the customer was not reached',
                            managerOnly: true,
                        },
                        align: 'right',
                    },
                    size: 120,
                }),
                columnHelper.accessor((row) => row.visitsPerRevenue, {
                    id: 'visitsPer100kRevenue',
                    header: t('table_header__visits_per_100k'),
                    cell: ({ getValue }) => {
                        const visitsPerRevenue = getValue();

                        return (
                            <DataText>
                                <NumberOrEmpty decimals={1}>
                                    {typeof visitsPerRevenue === 'number'
                                        ? visitsPerRevenue * 100_000
                                        : undefined}
                                </NumberOrEmpty>
                            </DataText>
                        );
                    },
                    meta: {
                        enabled: isManagerView,
                        sortBy: {
                            notesPerSalesMetric: {
                                notes: visitsFilter,
                                salesMetric: ttmRevenueFilter,
                            },
                        },
                        aiChat: {
                            description:
                                'Reached visits per 100k of billed revenue in the last 12 months. Empty below 1k revenue',
                            managerOnly: true,
                        },
                        align: 'right',
                        hint: t('visits_per_100k_tooltip'),
                        onHintOpen: () => {
                            trackVisitTooltipOpened('visits_per_100k');
                        },
                    },
                    size: 160,
                }),
                columnHelper.accessor((row) => row.visits?.lastOccurredAt, {
                    id: 'lastVisitDate',
                    header: t('table_header__last_visit'),
                    cell: ({ getValue }) => {
                        const lastVisitAt = getValue();
                        if (!lastVisitAt) {
                            return <DataText>-</DataText>;
                        }
                        return (
                            <DoubleDeckerCell
                                align="right"
                                topClassName="text-neutral-500"
                                bottomClassName="text-neutral-950"
                                topLevel={<FromNow>{lastVisitAt}</FromNow>}
                                bottomLevel={<DateText>{lastVisitAt}</DateText>}
                            />
                        );
                    },
                    meta: {
                        enabled: isManagerView,
                        sortBy: {
                            notes: {
                                field: 'last_occurred_at',
                                dimension: visitsFilter,
                            },
                        },
                        aiChat: { managerOnly: true },
                        align: 'right',
                        hint: t('last_visit_tooltip'),
                        onHintOpen: () => {
                            trackVisitTooltipOpened('last_visit');
                        },
                    },
                    size: 160,
                }),
                columnHelper.accessor((row) => row.opportunityInsights.totalAmount, {
                    id: 'opportunityInsightsAmount',
                    header: t('table_header__expansion_potential'),
                    cell: ({ getValue }) => (
                        <DataText>
                            <CurrencyOrEmpty showEmptyIfZero>{getValue()}</CurrencyOrEmpty>
                        </DataText>
                    ),

                    meta: {
                        enabled: isEqual(sorting.by, opportunityInsightsSortBy),
                        sortBy: opportunityInsightsSortBy,
                        aiChat: {
                            description:
                                "Potential revenue of the customer's open expansion opportunities. Shown when sorting by it",
                        },
                        align: 'right',
                    },
                }),
                columnHelper.accessor((row) => row.riskInsights.totalAmount, {
                    id: 'riskInsightsAmount',
                    header: t('table_header__revenue_decline'),
                    cell: ({ getValue }) => (
                        <DataText>
                            <CurrencyOrEmpty showEmptyIfZero>{getValue()}</CurrencyOrEmpty>
                        </DataText>
                    ),
                    meta: {
                        enabled: isEqual(sorting.by, riskInsightsSortBy),
                        sortBy: riskInsightsSortBy,
                        aiChat: {
                            description:
                                "Revenue decline flagged by the customer's open churn warnings. Shown when sorting by it",
                        },
                        align: 'right',
                    },
                }),
                columnHelper.accessor((row) => row.salesMetric.totalAmount, {
                    id: 'salesMetric',
                    header: defaultMetricTitle,
                    cell: ({ getValue }) => (
                        <DataText>
                            <CurrencyOrEmpty showEmptyIfZero>{getValue()}</CurrencyOrEmpty>
                        </DataText>
                    ),
                    meta: {
                        sortBy: {
                            salesMetric: { field: 'total_amount', dimension: defaultMetricFilter },
                        },
                        summary: (data) => (
                            <DataText>
                                <CurrencyOrEmpty showEmptyIfZero>
                                    {data.salesMetric?.totalAmount}
                                </CurrencyOrEmpty>
                            </DataText>
                        ),
                        align: 'right',
                        hint: <MetricHintExplanation metric={metricBase} />,
                        onHintOpen: () => {
                            trackMetricHintOpened(metricBase);
                        },
                    },
                }),
                columnHelper.accessor((row) => row.salesMetric.deltaTotalAmount, {
                    id: 'salesMetricDelta',
                    header: isDefaultTimeframeTtm
                        ? t('table_header__ttm_delta')
                        : t('table_header__revenue_ytd_delta'),
                    cell: ({ row }) => {
                        const metric = row.original.salesMetric;
                        const amount = metric.deltaTotalAmount;
                        const up = amount === 0 ? null : amount > 0;

                        return (
                            <DoubleDeckerCell
                                topLevel={
                                    <DataText>
                                        <DeltaText up={up}>
                                            <PercentageOrEmpty showEmptyIfZero showPlusSignIfPositive>
                                                {metric.deltaPercentTotalAmount}
                                            </PercentageOrEmpty>
                                            {up !== null && <DeltaTriangle up={up} />}
                                        </DeltaText>
                                    </DataText>
                                }
                                bottomLevel={
                                    <DataText>
                                        <CurrencyOrEmpty showEmptyIfZero showPlusSignIfPositive>
                                            {amount}
                                        </CurrencyOrEmpty>
                                    </DataText>
                                }
                            />
                        );
                    },
                    meta: {
                        sortBy: {
                            salesMetric: {
                                field: 'delta_total_amount',
                                dimension: defaultMetricFilter,
                            },
                        },
                        summary: (data) => {
                            const amount = data.salesMetric?.deltaTotalAmount ?? 0;
                            const percent = data.salesMetric?.deltaPercentTotalAmount;
                            const up = amount === 0 ? null : amount > 0;

                            return (
                                <DoubleDeckerCell
                                    topLevel={
                                        <DataText>
                                            <DeltaText up={up}>
                                                <PercentageOrEmpty showEmptyIfZero showPlusSignIfPositive>
                                                    {percent}
                                                </PercentageOrEmpty>
                                                {up !== null && <DeltaTriangle up={up} />}
                                            </DeltaText>
                                        </DataText>
                                    }
                                    bottomLevel={
                                        <DataText>
                                            <CurrencyOrEmpty showEmptyIfZero showPlusSignIfPositive>
                                                {amount}
                                            </CurrencyOrEmpty>
                                        </DataText>
                                    }
                                />
                            );
                        },
                        align: 'right',
                    },
                }),
                columnHelper.accessor((row) => row.lastOrder.lastDate, {
                    id: 'lastOrderDate',
                    header: t('table_header__last_order_date'),
                    cell: ({ getValue }) => {
                        const lastOrderDate = getValue();
                        if (!lastOrderDate) {
                            return <DataText>-</DataText>;
                        }
                        return (
                            <DoubleDeckerCell
                                align="left"
                                topClassName="text-neutral-500"
                                bottomClassName="text-neutral-950"
                                topLevel={<FromNow>{lastOrderDate}</FromNow>}
                                bottomLevel={<DateText>{lastOrderDate}</DateText>}
                            />
                        );
                    },
                    meta: {
                        align: 'left',
                    },
                }),
                columnHelper.accessor((row) => row.salesReps, {
                    id: 'salesReps',
                    meta: {
                        enabled: canReadSalesRepAssignment,
                        aiChat: {
                            description: 'All sales reps assigned to the customer',
                            requires: ['canReadSalesRepAssignment'],
                        },
                    },
                    header: t('table_header__primary_rep'),
                    cell: ({ getValue }) => {
                        const salesReps = getValue() ?? [];
                        return (
                            <span className="truncate">
                                {salesReps.length > 0
                                    ? salesReps.map((rep) => `${rep.id}: ${rep.name}`).join(', ')
                                    : '-'}
                            </span>
                        );
                    },
                }),
                columnHelper.accessor((row) => row.primaryAddress?.city, {
                    id: 'city',
                    header: t('table_header__city'),
                }),
                columnHelper.accessor((row) => row.industrySector, {
                    id: 'industrySector',
                    header: t('table_header__industry'),
                    cell: ({ getValue }) => getValue() || '-',
                }),
                columnHelper.accessor((row) => row.salesMetric.grossMargin, {
                    id: 'grossMargin',
                    header: () => (
                        <MaskedContent>
                            {isDefaultTimeframeTtm ? t('table_header__gm_ttm') : t('table_header__gm_ytd')}
                        </MaskedContent>
                    ),
                    cell: ({ getValue }) => (
                        <MaskedContent>
                            <DataText>
                                <CurrencyOrEmpty showEmptyIfZero>{getValue()}</CurrencyOrEmpty>
                            </DataText>
                        </MaskedContent>
                    ),
                    meta: {
                        enabled: canReadGrossMargin,
                        sortBy: {
                            salesMetric: { field: 'gross_margin', dimension: defaultMetricFilter },
                        },
                        summary: (data) => (
                            <MaskedContent>
                                <DataText>
                                    <CurrencyOrEmpty showEmptyIfZero>
                                        {data.salesMetric?.grossMargin}
                                    </CurrencyOrEmpty>
                                </DataText>
                            </MaskedContent>
                        ),
                        align: 'right',
                        hint: <MetricHintExplanation base={metricBase} metric="GROSS_MARGIN" />,
                        onHintOpen: () => {
                            trackMetricHintOpened('GROSS_MARGIN');
                        },
                    },
                }),
                columnHelper.accessor((row) => row.salesMetric, {
                    id: 'grossMarginPercent',
                    header: () => (
                        <MaskedContent>
                            {isDefaultTimeframeTtm
                                ? t('table_header__gm_ttm_percent')
                                : t('table_header__gm_ytd_percent')}
                        </MaskedContent>
                    ),
                    cell: ({ getValue }) => (
                        <MaskedContent>
                            <DataText>
                                <MarginPercentage
                                    margin={getValue().grossMargin}
                                    revenue={getValue().totalAmount}
                                />
                            </DataText>
                        </MaskedContent>
                    ),
                    meta: {
                        enabled: canReadGrossMargin,
                        summary: (data) => (
                            <MaskedContent>
                                <DataText>
                                    <MarginPercentage
                                        margin={data.salesMetric?.grossMargin}
                                        revenue={data.salesMetric?.totalAmount}
                                    />
                                </DataText>
                            </MaskedContent>
                        ),
                        align: 'right',
                        hint: <MetricHintExplanation base={metricBase} metric="GROSS_MARGIN_PERCENTAGE" />,
                        onHintOpen: () => {
                            trackMetricHintOpened('GROSS_MARGIN_PERCENTAGE');
                        },
                    },
                    size: 130,
                }),
                columnHelper.accessor((row) => row.salesMetric.deltaGrossMargin, {
                    id: 'grossMarginDelta',
                    header: () => (
                        <MaskedContent>
                            {isDefaultTimeframeTtm
                                ? t('table_header__gm_ttm_delta')
                                : t('table_header__gm_ytd_delta')}
                        </MaskedContent>
                    ),
                    cell: ({ row }) => {
                        const metric = row.original.salesMetric;
                        const amount = metric.deltaGrossMargin ?? 0;
                        const up = amount === 0 ? null : amount > 0;
                        return (
                            <MaskedContent>
                                <DoubleDeckerCell
                                    topLevel={
                                        <DataText>
                                            <DeltaText up={up}>
                                                <PercentageOrEmpty showEmptyIfZero showPlusSignIfPositive>
                                                    {metric.deltaPercentGrossMargin}
                                                </PercentageOrEmpty>
                                                {up !== null && <DeltaTriangle up={up} />}
                                            </DeltaText>
                                        </DataText>
                                    }
                                    bottomLevel={
                                        <DataText>
                                            <CurrencyOrEmpty showEmptyIfZero showPlusSignIfPositive>
                                                {amount}
                                            </CurrencyOrEmpty>
                                        </DataText>
                                    }
                                />
                            </MaskedContent>
                        );
                    },
                    meta: {
                        enabled: canReadGrossMargin,
                        sortBy: {
                            salesMetric: {
                                field: 'delta_gross_margin',
                                dimension: defaultMetricFilter,
                            },
                        },
                        summary: (data) => {
                            const amount = data.salesMetric?.deltaGrossMargin ?? 0;
                            const percent = data.salesMetric?.deltaPercentGrossMargin;
                            const up = amount === 0 ? null : amount > 0;
                            return (
                                <MaskedContent>
                                    <DoubleDeckerCell
                                        topLevel={
                                            <DataText>
                                                <DeltaText up={up}>
                                                    <PercentageOrEmpty showEmptyIfZero showPlusSignIfPositive>
                                                        {percent}
                                                    </PercentageOrEmpty>
                                                    {up !== null && <DeltaTriangle up={up} />}
                                                </DeltaText>
                                            </DataText>
                                        }
                                        bottomLevel={
                                            <DataText>
                                                <CurrencyOrEmpty showEmptyIfZero showPlusSignIfPositive>
                                                    {amount}
                                                </CurrencyOrEmpty>
                                            </DataText>
                                        }
                                    />
                                </MaskedContent>
                            );
                        },
                        align: 'right',
                    },
                }),
            ]),
        [
            canReadGrossMargin,
            canReadSalesRepAssignment,
            enablePlatoStatus,
            isManagerView,
            sorting.by,
            t,
            trackVisitTooltipOpened,
            isDefaultTimeframeTtm,
            defaultMetricFilter,
            defaultMetricTitle,
            metricBase,
            trackMetricHintOpened,
        ],
    );
}
